/**
 * @file Renders the table of investments being transferred, with an expandable row for their attachments.
 */
import { Table } from "antd";
import type { TableProps } from "antd";

import useColumns from "./helpers/useColumns";
import useDataSource from "./helpers/useDataSource";
import useAttachmentsCount from "./helpers/useAttachmentsCount";
import InvestmentAttachmentsTable from "./InvestmentAttachmentsTable";
import type {
  SetState,
  TableRef,
  Investment,
  Transaction,
  InvestorFile,
  CustomTarget,
  CustomAmounts,
  TransferInvestment,
} from "./types";

type Props = {
  table: TableRef;
  sourceFiles: InvestorFile[];
  customAmounts: CustomAmounts[];
  customTargets: CustomTarget[];
  sourceProjectId?: string;
  targetProjectId?: string;
  setCustomTargets: SetState<CustomTarget[]>;
  sourceInvestments: Investment[];
  targetInvestments: Investment[];
  sourceTransactions: Transaction[];
  getSourceBatchName: (batchId: string) => string | undefined;
};

/**
 * Renders the transfer table, with its columns, its rows and the expandable attachments for each investment.
 */
const InvestmentsTransferTable = ({
  table,
  sourceFiles,
  customAmounts,
  customTargets,
  sourceProjectId = undefined,
  targetProjectId = undefined,
  setCustomTargets,
  sourceInvestments,
  targetInvestments,
  sourceTransactions,
  getSourceBatchName,
}: Props) => {
  const getInvestmentAttachmentsCount = useAttachmentsCount({
    sourceFiles,
    sourceTransactions,
  });

  const columns = useColumns({
    targetProjectId,
    sourceProjectId,
    sourceInvestments,
    targetInvestments,
    getInvestmentAttachmentsCount,
    customAmounts,
    customTargets,
    setCustomTargets,
  });

  const dataSource = useDataSource({
    customTargets,
    sourceInvestments,
    targetInvestments,
  });

  /**
   * Exposes the current table rows to the parent through the shared table ref.
   */
  table.current.getDataSource = () => dataSource;

  /**
   * Renders the attachments table for the expanded investment row.
   */
  const expandedRowRender = (investment: TransferInvestment) => {
    const { id, isIncluded, sourceInvestmentId } = investment;

    let files: InvestorFile[] = [];
    let transactions: Transaction[] = [];
    let investmentId = id;
    let getBatchName = getSourceBatchName;

    if (isIncluded) {
      files = sourceFiles;
      transactions = sourceTransactions;
      investmentId = sourceInvestmentId || id;
      getBatchName = getSourceBatchName;
    }

    return (
      <InvestmentAttachmentsTable
        files={files}
        transactions={transactions}
        investmentId={investmentId}
        getBatchName={getBatchName}
      />
    );
  };

  const tableProps: TableProps<TransferInvestment> = {
    size: "middle",
    rowKey: "id",
    bordered: true,
    pagination: false,
    expandable: {
      /**
       * Reports whether the investment row has attachments to expand.
       */
      rowExpandable: (investment) => !!getInvestmentAttachmentsCount(investment),
      expandedRowRender,
    },
    columns,
    dataSource,
  };

  return (
    <Table {...tableProps} />
  );
};

export default InvestmentsTransferTable;
